import { useEffect } from 'react';

import { useFlowNavigation } from '../hooks/useFlowNavigation';
import ABA from '../pages/tools/games/ABA';
import { useFlowActions } from '../store/hooks';
import { FlowStep } from '../store/slices/flowSlice';

import LandingSplash from './LandingSplash';
import SceneDynamic from './SceneDynamic';
import ScenePlanning from './ScenePlanning';

/**
 * FlowController manages the sequential flow of the application,
 * enforcing step-by-step progression through the planning and editor phases.
 */
export default function FlowController() {
  const { currentStep } = useFlowNavigation();
  const flowActions = useFlowActions();

  // Validate and restore flow state on mount
  useEffect(() => {
    flowActions.validateAndRestoreFlow();
  }, [flowActions]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Prevent default browser navigation
      event.preventDefault();

      // Get the hash from the event
      const hash = window.location.hash.slice(1);
      if (hash) {
        const step = hash as FlowStep;

        // Only navigate if the step is allowed
        if (flowActions.canNavigateToStep(step)) {
          flowActions.goToStep(step);
        } else {
          // Restore the correct hash for current step
          window.location.hash = currentStep === FlowStep.LANDING ? '' : currentStep;
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [flowActions, currentStep]);

  // Render the appropriate component based on current step
  switch (currentStep) {
    case FlowStep.LANDING:
      return <LandingSplash />;

    case FlowStep.SCENE_DYNAMIC:
      return <SceneDynamic />;

    case FlowStep.SCENE_PLANNING:
      return <ScenePlanning />;

    case FlowStep.EDITOR:
      return <ABA />;

    default:
      // Fallback to landing page
      return <LandingSplash />;
  }
}
